import type { StepGuessRange } from '../components/FeaturedGuess.js';
import { DEFAULT_RELATIONSHIP_LENSES, type StoryEndpoints } from './url-state.js';

export const DEVICE_LIBRARY_KEY = 'entangled-vega:device-library:v1';
export const DEVICE_LIBRARY_VERSION = 1 as const;

export const DEVICE_LIBRARY_CAPS = {
  favoritePeople: 24,
  favoriteTraces: 24,
  recentPeople: 12,
  recentTraces: 12,
} as const;

export type StoredTraceReference = {
  from: string;
  to: string;
  lenses?: readonly ('personal' | 'family')[];
  storyId?: string;
};

export type StoredDailyChallengeResult = {
  datasetVersion: string;
  dayKey: `utc-day-${number}`;
  storyId: string;
  stepGuess?: StepGuessRange;
  bridgeChoiceId?: string;
  revealed: boolean;
  stepCorrect?: boolean;
  bridgeCorrect?: boolean;
};

export type DeviceLibraryState = {
  version: typeof DEVICE_LIBRARY_VERSION;
  datasetVersion: string;
  favoritePeople: readonly string[];
  favoriteTraces: readonly StoredTraceReference[];
  recentPeople: readonly string[];
  recentTraces: readonly StoredTraceReference[];
  dailyChallenge?: StoredDailyChallengeResult;
};

export type DeviceLibraryStorageMode = 'local' | 'session' | 'memory';

export type DeviceLibrarySnapshot = {
  state: DeviceLibraryState;
  mode: DeviceLibraryStorageMode;
  notice: string | null;
};

export type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export type DeviceLibraryValidation = {
  datasetVersion: string;
  validPersonIds: ReadonlySet<string>;
  validStories: ReadonlyMap<string, StoryEndpoints>;
  isValidTrace: (trace: StoredTraceReference) => boolean;
  preserveUnvalidatedReferences?: boolean;
  currentDayKey?: `utc-day-${number}`;
  currentStoryId?: string;
};

export type DeviceLibraryController = {
  snapshot(): DeviceLibrarySnapshot;
  reload(): DeviceLibrarySnapshot;
  addFavoritePerson(personId: string): DeviceLibrarySnapshot;
  removeFavoritePerson(personId: string): DeviceLibrarySnapshot;
  addRecentPerson(personId: string): DeviceLibrarySnapshot;
  removeRecentPerson(personId: string): DeviceLibrarySnapshot;
  addFavoriteTrace(trace: StoredTraceReference): DeviceLibrarySnapshot;
  removeFavoriteTrace(trace: StoredTraceReference): DeviceLibrarySnapshot;
  addRecentTrace(trace: StoredTraceReference): DeviceLibrarySnapshot;
  removeRecentTrace(trace: StoredTraceReference): DeviceLibrarySnapshot;
  clearRecents(): DeviceLibrarySnapshot;
  clearAll(): DeviceLibrarySnapshot;
  setDailyChallenge(result: StoredDailyChallengeResult | undefined): DeviceLibrarySnapshot;
};

const STEP_GUESSES = new Set<StepGuessRange>(['1-2', '3-4', '5-plus']);
const LENSES = new Set(['personal', 'family']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function qid(value: unknown): value is string {
  return nonEmptyString(value) && /^Q[1-9]\d*$/u.test(value);
}

function defaultState(datasetVersion: string): DeviceLibraryState {
  return {
    version: DEVICE_LIBRARY_VERSION,
    datasetVersion,
    favoritePeople: [],
    favoriteTraces: [],
    recentPeople: [],
    recentTraces: [],
  };
}

function normalizedLenses(value: unknown): readonly ('personal' | 'family')[] | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (
    !Array.isArray(value) ||
    value.length === 0 ||
    value.some((lens) => typeof lens !== 'string' || !LENSES.has(lens))
  ) {
    return undefined;
  }
  const lenses = ['personal', 'family'].filter((lens) => value.includes(lens)) as (
    'personal' | 'family'
  )[];
  return lenses.length === DEFAULT_RELATIONSHIP_LENSES.length ? undefined : lenses;
}

function parseTrace(value: unknown): StoredTraceReference | null {
  if (!isRecord(value) || !qid(value.from) || !qid(value.to) || value.from === value.to) {
    return null;
  }
  const lenses = normalizedLenses(value.lenses);
  if (value.lenses !== undefined && lenses === undefined) {
    const supplied = Array.isArray(value.lenses) ? value.lenses : [];
    if (
      supplied.length !== DEFAULT_RELATIONSHIP_LENSES.length ||
      !DEFAULT_RELATIONSHIP_LENSES.every((lens) => supplied.includes(lens))
    ) {
      return null;
    }
  }
  if (value.storyId !== undefined && !nonEmptyString(value.storyId)) {
    return null;
  }
  return {
    from: value.from,
    to: value.to,
    ...(lenses === undefined ? {} : { lenses }),
    ...(typeof value.storyId === 'string' ? { storyId: value.storyId } : {}),
  };
}

function parseDailyChallenge(value: unknown): StoredDailyChallengeResult | undefined {
  if (
    !isRecord(value) ||
    !nonEmptyString(value.datasetVersion) ||
    !nonEmptyString(value.dayKey) ||
    !/^utc-day--?\d+$/u.test(value.dayKey) ||
    !nonEmptyString(value.storyId) ||
    typeof value.revealed !== 'boolean' ||
    (value.stepGuess !== undefined &&
      (typeof value.stepGuess !== 'string' ||
        !STEP_GUESSES.has(value.stepGuess as StepGuessRange))) ||
    (value.bridgeChoiceId !== undefined && !qid(value.bridgeChoiceId)) ||
    (value.stepCorrect !== undefined && typeof value.stepCorrect !== 'boolean') ||
    (value.bridgeCorrect !== undefined && typeof value.bridgeCorrect !== 'boolean')
  ) {
    return undefined;
  }
  return {
    datasetVersion: value.datasetVersion,
    dayKey: value.dayKey as `utc-day-${number}`,
    storyId: value.storyId,
    revealed: value.revealed,
    ...(value.stepGuess === undefined ? {} : { stepGuess: value.stepGuess as StepGuessRange }),
    ...(typeof value.bridgeChoiceId === 'string' ? { bridgeChoiceId: value.bridgeChoiceId } : {}),
    ...(typeof value.stepCorrect === 'boolean' ? { stepCorrect: value.stepCorrect } : {}),
    ...(typeof value.bridgeCorrect === 'boolean' ? { bridgeCorrect: value.bridgeCorrect } : {}),
  };
}

function parseStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter(qid) : [];
}

function parseTraceArray(value: unknown): StoredTraceReference[] {
  return Array.isArray(value)
    ? value.map(parseTrace).filter((trace): trace is StoredTraceReference => trace !== null)
    : [];
}

export function decodeDeviceLibrary(
  raw: string | null,
  datasetVersion: string,
): DeviceLibraryState {
  if (raw === null) {
    return defaultState(datasetVersion);
  }
  let value: unknown;
  try {
    value = JSON.parse(raw) as unknown;
  } catch {
    return defaultState(datasetVersion);
  }
  if (
    !isRecord(value) ||
    value.version !== DEVICE_LIBRARY_VERSION ||
    !nonEmptyString(value.datasetVersion)
  ) {
    return defaultState(datasetVersion);
  }
  const dailyChallenge = parseDailyChallenge(value.dailyChallenge);
  return {
    version: DEVICE_LIBRARY_VERSION,
    datasetVersion: value.datasetVersion,
    favoritePeople: parseStringArray(value.favoritePeople),
    favoriteTraces: parseTraceArray(value.favoriteTraces),
    recentPeople: parseStringArray(value.recentPeople),
    recentTraces: parseTraceArray(value.recentTraces),
    ...(dailyChallenge === undefined ? {} : { dailyChallenge }),
  };
}

function uniqueFront<T>(
  values: readonly T[],
  identity: (value: T) => string,
  maximum: number,
): T[] {
  const seen = new Set<string>();
  const unique: T[] = [];
  for (const value of values) {
    const key = identity(value);
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(value);
    }
    if (unique.length >= maximum) {
      break;
    }
  }
  return unique;
}

export function traceReferenceKey(trace: StoredTraceReference): string {
  return [
    trace.from,
    trace.to,
    trace.storyId ?? '',
    ...(trace.lenses ?? DEFAULT_RELATIONSHIP_LENSES),
  ].join(':');
}

function validTrace(trace: StoredTraceReference, validation: DeviceLibraryValidation): boolean {
  if (
    !validation.validPersonIds.has(trace.from) ||
    !validation.validPersonIds.has(trace.to) ||
    !validation.isValidTrace(trace)
  ) {
    return false;
  }
  if (trace.storyId === undefined) {
    return true;
  }
  const story = validation.validStories.get(trace.storyId);
  return story?.source === trace.from && story.target === trace.to;
}

export function pruneDeviceLibrary(
  state: DeviceLibraryState,
  validation: DeviceLibraryValidation,
): DeviceLibraryState {
  const validPeople = (values: readonly string[], maximum: number) =>
    uniqueFront(
      values.filter(
        (personId) =>
          validation.preserveUnvalidatedReferences === true ||
          validation.validPersonIds.has(personId),
      ),
      (personId) => personId,
      maximum,
    );
  const validTraces = (values: readonly StoredTraceReference[], maximum: number) =>
    uniqueFront(
      values.filter(
        (trace) =>
          validation.preserveUnvalidatedReferences === true || validTrace(trace, validation),
      ),
      traceReferenceKey,
      maximum,
    );
  const dailyChallenge =
    state.dailyChallenge?.datasetVersion === validation.datasetVersion &&
    (validation.currentDayKey === undefined ||
      state.dailyChallenge.dayKey === validation.currentDayKey) &&
    (validation.currentStoryId === undefined ||
      state.dailyChallenge.storyId === validation.currentStoryId)
      ? state.dailyChallenge
      : undefined;
  return {
    version: DEVICE_LIBRARY_VERSION,
    datasetVersion: validation.datasetVersion,
    favoritePeople: validPeople(state.favoritePeople, DEVICE_LIBRARY_CAPS.favoritePeople),
    favoriteTraces: validTraces(state.favoriteTraces, DEVICE_LIBRARY_CAPS.favoriteTraces),
    recentPeople: validPeople(state.recentPeople, DEVICE_LIBRARY_CAPS.recentPeople),
    recentTraces: validTraces(state.recentTraces, DEVICE_LIBRARY_CAPS.recentTraces),
    ...(dailyChallenge === undefined ? {} : { dailyChallenge }),
  };
}

function moveToFront<T>(
  values: readonly T[],
  value: T,
  identity: (entry: T) => string,
  maximum: number,
): T[] {
  const key = identity(value);
  return [value, ...values.filter((entry) => identity(entry) !== key)].slice(0, maximum);
}

function sameState(left: DeviceLibraryState, right: DeviceLibraryState): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function createDeviceLibraryController(options: {
  validation: DeviceLibraryValidation;
  localStorage?: StorageLike | null;
  sessionStorage?: StorageLike | null;
}): DeviceLibraryController {
  let mode: DeviceLibraryStorageMode =
    options.localStorage !== undefined && options.localStorage !== null
      ? 'local'
      : options.sessionStorage !== undefined && options.sessionStorage !== null
        ? 'session'
        : 'memory';
  let notice: string | null =
    mode === 'local'
      ? null
      : mode === 'session'
        ? 'Saved activity is available only in this tab because local storage is unavailable.'
        : 'Saved activity is available only until this page closes because storage is unavailable.';
  let memoryRaw: string | null = null;
  const storageForMode = (): StorageLike | null =>
    mode === 'local'
      ? (options.localStorage ?? null)
      : mode === 'session'
        ? (options.sessionStorage ?? null)
        : null;

  function fallBack(): void {
    if (
      mode === 'local' &&
      options.sessionStorage !== undefined &&
      options.sessionStorage !== null
    ) {
      mode = 'session';
      notice = 'Saved activity is available only in this tab because local storage is unavailable.';
    } else {
      mode = 'memory';
      notice =
        'Saved activity is available only until this page closes because storage is unavailable.';
    }
  }

  function readRaw(): string | null {
    for (let attempts = 0; attempts < 3; attempts += 1) {
      const storage = storageForMode();
      if (storage === null) {
        return memoryRaw;
      }
      try {
        return storage.getItem(DEVICE_LIBRARY_KEY);
      } catch {
        fallBack();
      }
    }
    return memoryRaw;
  }

  function writeRaw(raw: string): void {
    for (let attempts = 0; attempts < 3; attempts += 1) {
      const storage = storageForMode();
      if (storage === null) {
        memoryRaw = raw;
        return;
      }
      try {
        storage.setItem(DEVICE_LIBRARY_KEY, raw);
        return;
      } catch {
        fallBack();
      }
    }
    memoryRaw = raw;
  }

  function decodeAndPrune(raw: string | null): DeviceLibraryState {
    return pruneDeviceLibrary(
      decodeDeviceLibrary(raw, options.validation.datasetVersion),
      options.validation,
    );
  }

  function read(): DeviceLibraryState {
    return decodeAndPrune(readRaw());
  }

  const initialRaw = readRaw();
  let state = decodeAndPrune(initialRaw);
  const initialSerialized = JSON.stringify(state);
  if (initialRaw !== null && initialRaw !== initialSerialized) {
    writeRaw(initialSerialized);
  }

  function snapshot(): DeviceLibrarySnapshot {
    return { state, mode, notice };
  }

  function update(transform: (current: DeviceLibraryState) => DeviceLibraryState) {
    const next = pruneDeviceLibrary(transform(state), options.validation);
    if (!sameState(state, next)) {
      state = next;
      writeRaw(JSON.stringify(state));
    }
    return snapshot();
  }

  function validPerson(personId: string): boolean {
    return options.validation.validPersonIds.has(personId);
  }

  function validReference(trace: StoredTraceReference): StoredTraceReference | null {
    const parsed = parseTrace(trace);
    return parsed !== null && validTrace(parsed, options.validation) ? parsed : null;
  }

  return {
    snapshot,
    reload() {
      state = read();
      return snapshot();
    },
    addFavoritePerson(personId) {
      return !validPerson(personId)
        ? snapshot()
        : update((current) => ({
            ...current,
            favoritePeople: moveToFront(
              current.favoritePeople,
              personId,
              (value) => value,
              DEVICE_LIBRARY_CAPS.favoritePeople,
            ),
          }));
    },
    removeFavoritePerson(personId) {
      return update((current) => ({
        ...current,
        favoritePeople: current.favoritePeople.filter((value) => value !== personId),
      }));
    },
    addRecentPerson(personId) {
      return !validPerson(personId)
        ? snapshot()
        : update((current) => ({
            ...current,
            recentPeople: moveToFront(
              current.recentPeople,
              personId,
              (value) => value,
              DEVICE_LIBRARY_CAPS.recentPeople,
            ),
          }));
    },
    removeRecentPerson(personId) {
      return update((current) => ({
        ...current,
        recentPeople: current.recentPeople.filter((value) => value !== personId),
      }));
    },
    addFavoriteTrace(trace) {
      const reference = validReference(trace);
      return reference === null
        ? snapshot()
        : update((current) => ({
            ...current,
            favoriteTraces: moveToFront(
              current.favoriteTraces,
              reference,
              traceReferenceKey,
              DEVICE_LIBRARY_CAPS.favoriteTraces,
            ),
          }));
    },
    removeFavoriteTrace(trace) {
      const key = traceReferenceKey(trace);
      return update((current) => ({
        ...current,
        favoriteTraces: current.favoriteTraces.filter((value) => traceReferenceKey(value) !== key),
      }));
    },
    addRecentTrace(trace) {
      const reference = validReference(trace);
      return reference === null
        ? snapshot()
        : update((current) => ({
            ...current,
            recentTraces: moveToFront(
              current.recentTraces,
              reference,
              traceReferenceKey,
              DEVICE_LIBRARY_CAPS.recentTraces,
            ),
          }));
    },
    removeRecentTrace(trace) {
      const key = traceReferenceKey(trace);
      return update((current) => ({
        ...current,
        recentTraces: current.recentTraces.filter((value) => traceReferenceKey(value) !== key),
      }));
    },
    clearRecents() {
      return update((current) => ({
        ...current,
        recentPeople: [],
        recentTraces: [],
      }));
    },
    clearAll() {
      const empty = defaultState(options.validation.datasetVersion);
      state = empty;
      for (const storage of [options.localStorage, options.sessionStorage]) {
        if (storage !== undefined && storage !== null) {
          try {
            storage.removeItem(DEVICE_LIBRARY_KEY);
          } catch {
            // The in-memory reset remains authoritative for this page.
          }
        }
      }
      memoryRaw = null;
      return snapshot();
    },
    setDailyChallenge(result) {
      return update((current) => {
        const next = { ...current };
        if (result === undefined) {
          delete next.dailyChallenge;
        } else {
          next.dailyChallenge = result;
        }
        return next;
      });
    },
  };
}

/**
 * Browser storage is resolved only when this function is called, never during
 * module import or server/static rendering.
 */
export function createBrowserDeviceLibraryController(
  validation: DeviceLibraryValidation,
): DeviceLibraryController {
  let localStorage: StorageLike | null = null;
  let sessionStorage: StorageLike | null = null;
  if (typeof window !== 'undefined') {
    try {
      localStorage = window.localStorage;
    } catch {
      localStorage = null;
    }
    try {
      sessionStorage = window.sessionStorage;
    } catch {
      sessionStorage = null;
    }
  }
  return createDeviceLibraryController({ validation, localStorage, sessionStorage });
}
